import { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Megaphone, Copy, Check, ExternalLink, Image, Eye, X, Video } from 'lucide-react';
import { adApi, appApi } from '@/api/client';
import type { Ad, App } from '@/types';

const positionLabels: Record<string, string> = {
  top: '顶部横幅',
  bottom: '底部横幅',
  popup: '弹窗广告',
  banner: '内嵌横幅',
  preroll: '前贴片广告',
  midroll: '中插广告',
  overlay: '播放器覆盖层',
  splash: '开屏广告',
};

// 需要倒计时/时长设置的位置
const timedPositions = ['preroll', 'midroll', 'splash'];

export default function Ads() {
  const [ads, setAds] = useState<Ad[]>([]);
  const [apps, setApps] = useState<App[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedApp, setSelectedApp] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingAd, setEditingAd] = useState<Ad | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [previewAd, setPreviewAd] = useState<Ad | null>(null);
  const [formData, setFormData] = useState({
    app_id: '',
    title: '',
    image_url: '',
    link_url: '',
    position: 'banner' as Ad['position'],
    sort_order: 0,
    is_active: true,
    start_time: '',
    end_time: '',
    // 视频广告扩展字段
    ad_type: 'image' as 'image' | 'video',
    video_url: '',
    duration: 15,
    skip_after: 5,
    auto_close: true,
    midroll_time: 0,
    close_after: 10,
  });

  useEffect(() => { fetchAds(); }, []);

  const fetchAds = async () => {
    setLoading(true);
    try {
      const [adsRes, appsRes] = await Promise.all([adApi.getAll(), appApi.getAll()]);
      if (adsRes.success) setAds(adsRes.data);
      if (appsRes.success) setApps(appsRes.data);
    } catch { console.error('Failed to fetch ads'); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingAd) {
        await adApi.update(editingAd.id, formData);
      } else {
        await adApi.create(formData);
      }
      setShowModal(false);
      setEditingAd(null);
      resetForm();
      fetchAds();
    } catch { alert('操作失败'); }
  };

  const handleDelete = async (id: string) => {
    if (confirm('确定要删除这个广告吗？')) {
      try { await adApi.delete(id); fetchAds(); } catch { alert('删除失败'); }
    }
  };

  const openEditModal = (ad: Ad) => {
    setEditingAd(ad);
    setFormData({
      app_id: ad.app_id,
      title: ad.title,
      image_url: ad.image_url,
      link_url: ad.link_url,
      position: ad.position,
      sort_order: ad.sort_order,
      is_active: ad.is_active,
      start_time: ad.start_time ? ad.start_time.slice(0, 16) : '',
      end_time: ad.end_time ? ad.end_time.slice(0, 16) : '',
      ad_type: ad.ad_type || 'image',
      video_url: ad.video_url || '',
      duration: ad.duration || 15,
      skip_after: ad.skip_after !== undefined ? ad.skip_after : 5,
      auto_close: ad.auto_close !== undefined ? ad.auto_close : true,
      midroll_time: ad.midroll_time || 0,
      close_after: ad.close_after || 10,
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({ app_id: '', title: '', image_url: '', link_url: '', position: 'banner', sort_order: 0, is_active: true, start_time: '', end_time: '', ad_type: 'image', video_url: '', duration: 15, skip_after: 5, auto_close: true, midroll_time: 0, close_after: 10 });
  };

  const handleCopy = async (text: string, id: string) => {
    await navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const filteredAds = selectedApp ? ads.filter(a => a.app_id === selectedApp) : ads;
  const getAppName = (appId: string) => apps.find(a => a.id === appId)?.name || '未知应用';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">广告管理</h1>
          <p className="text-slate-400 mt-1">为各应用添加和管理广告</p>
        </div>
        <button onClick={() => { resetForm(); setShowModal(true); }} className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-all">
          <Plus className="w-5 h-5" />新建广告
        </button>
      </div>

      <div className="flex gap-4">
        <select value={selectedApp} onChange={e => setSelectedApp(e.target.value)} className="px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500">
          <option value="">全部应用</option>
          {apps.map(app => <option key={app.id} value={app.id}>{app.name}</option>)}
        </select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? (
          [1, 2, 3].map(i => <div key={i} className="bg-slate-800 rounded-xl border border-slate-700/50 p-4 animate-pulse"><div className="h-40 bg-slate-700 rounded-lg mb-3" /><div className="h-4 bg-slate-700 rounded w-3/4 mb-2" /><div className="h-3 bg-slate-700 rounded w-1/2" /></div>)
        ) : filteredAds.length > 0 ? (
          filteredAds.map(ad => (
            <div key={ad.id} className="bg-slate-800 rounded-xl border border-slate-700/50 overflow-hidden hover:border-slate-600 transition-all">
              <div className="h-40 bg-slate-700 flex items-center justify-center relative">
                {ad.ad_type === 'video' && ad.video_url ? (
                  <video src={ad.video_url} className="w-full h-full object-cover" muted loop autoPlay playsInline />
                ) : ad.image_url ? (
                  <img src={ad.image_url} alt={ad.title} className="w-full h-full object-cover" />
                ) : (
                  <Image className="w-10 h-10 text-slate-500" />
                )}
                <span className={`absolute top-2 right-2 px-2 py-1 rounded text-xs font-medium ${ad.is_active ? 'bg-green-500/80 text-white' : 'bg-red-500/80 text-white'}`}>
                  {ad.is_active ? '启用' : '禁用'}
                </span>
                {ad.ad_type === 'video' && (
                  <span className="absolute top-2 left-2 px-2 py-1 rounded text-xs font-medium bg-purple-600/80 text-white flex items-center gap-1">
                    <Video className="w-3 h-3" /> 视频
                  </span>
                )}
                {ad.duration ? (
                  <span className="absolute bottom-2 right-2 px-2 py-1 rounded text-xs font-medium bg-black/60 text-white">{ad.duration}s</span>
                ) : null}
              </div>
              <div className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-white font-medium truncate">{ad.title}</h3>
                  <span className="text-xs px-2 py-1 bg-blue-500/20 text-blue-300 rounded whitespace-nowrap">{positionLabels[ad.position] || ad.position}</span>
                </div>
                <p className="text-slate-400 text-sm mb-2">所属应用：{getAppName(ad.app_id)}</p>
                <p className="text-slate-500 text-xs truncate mb-3">{ad.link_url || '无跳转链接'}</p>
                <div className="flex items-center justify-between">
                  <span className="text-slate-500 text-xs">
                    排序：{ad.sort_order}
                    {ad.skip_after ? ` · ${ad.skip_after}s可跳过` : ''}
                  </span>
                  <div className="flex gap-1">
                    <button onClick={() => setPreviewAd(ad)} className="p-2 text-slate-400 hover:text-green-400 hover:bg-green-500/10 rounded-lg transition-all" title="预览效果"><Eye className="w-4 h-4" /></button>
                    <button onClick={() => openEditModal(ad)} className="p-2 text-slate-400 hover:text-blue-400 hover:bg-blue-500/10 rounded-lg transition-all"><Edit className="w-4 h-4" /></button>
                    <button onClick={() => handleDelete(ad.id)} className="p-2 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="col-span-3 text-center py-12 text-slate-400">
            <Megaphone className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>暂无广告数据</p>
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-xl w-full max-w-lg border border-slate-700/50 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-700/50">
              <h2 className="text-xl font-bold text-white">{editingAd ? '编辑广告' : '新建广告'}</h2>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">所属应用</label>
                <select value={formData.app_id} onChange={e => setFormData({ ...formData, app_id: e.target.value })} className="w-full px-4 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500" required>
                  <option value="">请选择应用</option>
                  {apps.map(app => <option key={app.id} value={app.id}>{app.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">广告标题</label>
                <input type="text" value={formData.title} onChange={e => setFormData({ ...formData, title: e.target.value })} className="w-full px-4 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500" required />
              </div>

              {/* 广告类型选择 */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">广告类型</label>
                <div className="flex gap-2">
                  <button type="button" onClick={() => setFormData({ ...formData, ad_type: 'image' })} className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg border transition-all ${formData.ad_type === 'image' ? 'bg-blue-600/30 border-blue-500 text-blue-300' : 'bg-slate-700/50 border-slate-600 text-slate-400'}`}>
                    <Image className="w-4 h-4" /> 图片广告
                  </button>
                  <button type="button" onClick={() => setFormData({ ...formData, ad_type: 'video' })} className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg border transition-all ${formData.ad_type === 'video' ? 'bg-purple-600/30 border-purple-500 text-purple-300' : 'bg-slate-700/50 border-slate-600 text-slate-400'}`}>
                    <Video className="w-4 h-4" /> 视频广告
                  </button>
                </div>
              </div>

              {/* 视频URL（ad_type=video 时显示） */}
              {formData.ad_type === 'video' && (
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">视频广告 URL</label>
                  <input type="url" value={formData.video_url} onChange={e => setFormData({ ...formData, video_url: e.target.value })} placeholder="https://example.com/ad.mp4" className="w-full px-4 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500" required={formData.ad_type === 'video'} />
                  <p className="text-xs text-slate-500 mt-1">支持 MP4/WebM 等视频格式，托管在七牛云等 CDN</p>
                </div>
              )}

              {/* 图片URL（ad_type=image 时必填，video 时可选作为封面） */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">{formData.ad_type === 'video' ? '封面图片 URL（可选）' : '图片 URL'}</label>
                <input type="text" value={formData.image_url} onChange={e => setFormData({ ...formData, image_url: e.target.value })} placeholder="https://example.com/ad.jpg" className="w-full px-4 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500" required={formData.ad_type === 'image'} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">跳转链接</label>
                <input type="text" value={formData.link_url} onChange={e => setFormData({ ...formData, link_url: e.target.value })} placeholder="https://example.com" className="w-full px-4 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">广告位置</label>
                  <select value={formData.position} onChange={e => setFormData({ ...formData, position: e.target.value as Ad['position'] })} className="w-full px-4 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <optgroup label="基础位置">
                      <option value="top">顶部横幅</option>
                      <option value="bottom">底部横幅</option>
                      <option value="popup">弹窗广告</option>
                      <option value="banner">内嵌横幅</option>
                    </optgroup>
                    <optgroup label="视频播放广告">
                      <option value="preroll">前贴片广告</option>
                      <option value="midroll">中插广告</option>
                      <option value="overlay">播放器覆盖层</option>
                      <option value="splash">开屏广告</option>
                    </optgroup>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">排序</label>
                  <input type="number" value={formData.sort_order} onChange={e => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })} className="w-full px-4 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>

              {/* 中插广告：插入时间点 */}
              {formData.position === 'midroll' && (
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">中插时间点（正片第几秒插入）</label>
                  <input type="number" value={formData.midroll_time} onChange={e => setFormData({ ...formData, midroll_time: parseInt(e.target.value) || 0 })} min="0" placeholder="如 30 表示正片播放30秒时插入" className="w-full px-4 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              )}

              {/* 倒计时/时长设置（前贴片/中插/开屏） */}
              {timedPositions.includes(formData.position) && (
                <div className="bg-slate-900/50 rounded-lg p-4 space-y-3 border border-slate-700">
                  <h4 className="text-sm font-semibold text-amber-400 flex items-center gap-2"><Video className="w-4 h-4" /> 倒计时与跳过设置</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">广告时长（秒）</label>
                      <input type="number" value={formData.duration} onChange={e => setFormData({ ...formData, duration: parseInt(e.target.value) || 0 })} min="1" className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-500" />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">可跳过秒数</label>
                      <input type="number" value={formData.skip_after} onChange={e => setFormData({ ...formData, skip_after: parseInt(e.target.value) || 0 })} min="0" placeholder="几秒后显示跳过按钮" className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-500" />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <input type="checkbox" id="auto_close" checked={formData.auto_close} onChange={e => setFormData({ ...formData, auto_close: e.target.checked })} className="w-4 h-4 rounded" />
                    <label htmlFor="auto_close" className="text-sm text-slate-300">倒计时结束后自动关闭</label>
                  </div>
                </div>
              )}

              {/* 覆盖层：自动关闭秒数 */}
              {formData.position === 'overlay' && (
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">自动关闭秒数</label>
                  <input type="number" value={formData.close_after} onChange={e => setFormData({ ...formData, close_after: parseInt(e.target.value) || 0 })} min="1" placeholder="多少秒后自动消失" className="w-full px-4 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">开始时间</label>
                  <input type="datetime-local" value={formData.start_time} onChange={e => setFormData({ ...formData, start_time: e.target.value })} className="w-full px-4 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">结束时间</label>
                  <input type="datetime-local" value={formData.end_time} onChange={e => setFormData({ ...formData, end_time: e.target.value })} className="w-full px-4 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="is_active" checked={formData.is_active} onChange={e => setFormData({ ...formData, is_active: e.target.checked })} className="w-4 h-4 rounded" />
                <label htmlFor="is_active" className="text-sm text-slate-300">启用广告</label>
              </div>
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => { setShowModal(false); setEditingAd(null); resetForm(); }} className="flex-1 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-all">取消</button>
                <button type="submit" className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-all">{editingAd ? '保存' : '创建'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 广告效果预览 */}
      {previewAd && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setPreviewAd(null)}>
          <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-red-400" />
                <div className="w-3 h-3 rounded-full bg-yellow-400" />
                <div className="w-3 h-3 rounded-full bg-green-400" />
                <span className="ml-2 text-sm text-gray-500">{getAppName(previewAd.app_id)}</span>
              </div>
              <button onClick={() => setPreviewAd(null)} className="p-1 hover:bg-gray-200 rounded"><X className="w-4 h-4 text-gray-500" /></button>
            </div>
            <div className="relative">
              {previewAd.position === 'top' && (
                <div>
                  <a href={previewAd.link_url} target="_blank" rel="noopener noreferrer" className="block">
                    <img src={previewAd.image_url} alt={previewAd.title} className="w-full h-32 object-cover" />
                  </a>
                  <div className="p-4 text-center text-gray-400 text-sm">↓ 应用内容区域 ↓</div>
                </div>
              )}
              {previewAd.position === 'bottom' && (
                <div>
                  <div className="p-4 text-center text-gray-400 text-sm">↑ 应用内容区域 ↑</div>
                  <a href={previewAd.link_url} target="_blank" rel="noopener noreferrer" className="block">
                    <img src={previewAd.image_url} alt={previewAd.title} className="w-full h-32 object-cover" />
                  </a>
                </div>
              )}
              {previewAd.position === 'popup' && (
                <div className="relative min-h-[300px] bg-gray-100 flex items-center justify-center">
                  <div className="text-gray-400 text-sm">应用内容区域</div>
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                    <div className="bg-white rounded-xl overflow-hidden shadow-xl max-w-[280px]">
                      <a href={previewAd.link_url} target="_blank" rel="noopener noreferrer">
                        <img src={previewAd.image_url} alt={previewAd.title} className="w-full h-36 object-cover" />
                      </a>
                      <div className="p-3">
                        <h3 className="text-sm font-medium text-gray-800">{previewAd.title}</h3>
                        <button onClick={() => setPreviewAd(null)} className="mt-2 text-xs text-gray-400">关闭</button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {previewAd.position === 'banner' && (
                <div className="p-4">
                  <div className="text-gray-400 text-sm text-center mb-3">应用内容区域</div>
                  <a href={previewAd.link_url} target="_blank" rel="noopener noreferrer" className="block rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-shadow">
                    <img src={previewAd.image_url} alt={previewAd.title} className="w-full h-40 object-cover" />
                    <div className="bg-white p-2"><h3 className="text-sm font-medium text-gray-800">{previewAd.title}</h3></div>
                  </a>
                </div>
              )}
              {/* 前贴片 / 中插 / 开屏 预览（带倒计时） */}
              {(previewAd.position === 'preroll' || previewAd.position === 'midroll' || previewAd.position === 'splash') && (
                <div className="relative bg-black">
                  {previewAd.ad_type === 'video' && previewAd.video_url ? (
                    <video src={previewAd.video_url} className="w-full h-56 object-contain bg-black" autoPlay muted loop playsInline />
                  ) : (
                    <img src={previewAd.image_url} alt={previewAd.title} className="w-full h-56 object-cover" />
                  )}
                  {/* 倒计时模拟 */}
                  <div className="absolute top-3 right-3 bg-black/70 text-white text-xs px-3 py-1.5 rounded-full flex items-center gap-2">
                    <span className="text-amber-400">广告 {previewAd.duration || 15}s</span>
                    <span className="text-blue-400 border-l border-white/30 pl-2">跳过</span>
                  </div>
                  {previewAd.position === 'preroll' && <div className="absolute bottom-3 left-3 text-white/60 text-xs">正片即将开始…</div>}
                  {previewAd.position === 'midroll' && <div className="absolute bottom-3 left-3 text-white/60 text-xs">正片将在广告结束后继续</div>}
                  {previewAd.position === 'splash' && <div className="absolute bottom-3 left-1/2 -translate-x-1/2 text-white/60 text-xs">{previewAd.skip_after || 3}s 后自动关闭</div>}
                </div>
              )}
              {/* 覆盖层预览 */}
              {previewAd.position === 'overlay' && (
                <div className="relative bg-black h-56 flex items-center justify-center">
                  <div className="text-gray-500 text-sm">视频播放区域</div>
                  <div className="absolute bottom-3 right-3 bg-white/95 rounded-lg shadow-lg overflow-hidden max-w-[200px]">
                    <a href={previewAd.link_url} target="_blank" rel="noopener noreferrer">
                      {previewAd.ad_type === 'video' && previewAd.video_url ? (
                        <video src={previewAd.video_url} className="w-full h-24 object-cover" muted loop autoPlay playsInline />
                      ) : (
                        <img src={previewAd.image_url} alt={previewAd.title} className="w-full h-24 object-cover" />
                      )}
                    </a>
                    <div className="p-1.5">
                      <h3 className="text-xs font-medium text-gray-800 truncate">{previewAd.title}</h3>
                      <span className="text-[10px] text-gray-400">{previewAd.close_after || 10}s 后关闭</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="px-4 py-3 bg-gray-50 border-t text-xs text-gray-400 flex justify-between">
              <span>位置：{positionLabels[previewAd.position]}</span>
              <span>{previewAd.is_active ? '启用中' : '已禁用'}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}